import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

const faqs = [
  {
    question: 'Wie schnell erhalte ich ein Angebot?',
    answer: 'In der Regel erhalten Sie innerhalb weniger Stunden eine erste Einschätzung. Bei vollständigen Angaben oft sogar innerhalb von 30 Minuten. Wir sind 24/7 erreichbar.',
  },
  {
    question: 'Kauft ihr auch Unfallwagen?',
    answer: 'Ja, wir kaufen auch Unfallwagen an – egal ob mit kleinen Blechschäden oder größeren Beschädigungen. Wir machen Ihnen in jedem Fall ein faires Angebot.',
  },
  {
    question: 'Kauft ihr Fahrzeuge ohne TÜV?',
    answer: 'Selbstverständlich! Ein abgelaufener oder fehlender TÜV ist für uns kein Problem. Wir bewerten Ihr Fahrzeug unabhängig vom TÜV-Status.',
  },
  {
    question: 'Muss ich vorbeikommen?',
    answer: 'Nein, das ist nicht zwingend notwendig. Wir können einen Termin bei Ihnen vor Ort vereinbaren oder Ihr Fahrzeug auf Wunsch direkt abholen.',
  },
  {
    question: 'Kann mein Auto abgeholt werden?',
    answer: 'Ja, wir bieten einen kostenlosen Abholservice deutschlandweit an. Auch europaweit holen wir Ihr Fahrzeug nach Absprache ab.',
  },
  {
    question: 'Welche Unterlagen brauche ich?',
    answer: 'Für den Verkauf benötigen Sie: Fahrzeugschein (Zulassungsbescheinigung Teil I), Fahrzeugbrief (Zulassungsbescheinigung Teil II), alle vorhandenen Schlüssel und wenn möglich das Serviceheft.',
  },
  {
    question: 'Ist die Anfrage kostenlos?',
    answer: 'Ja, die Bewertung und Anfrage sind zu 100% kostenlos und unverbindlich. Sie gehen keinerlei Verpflichtung ein.',
  },
  {
    question: 'Kauft ihr auch Autos mit Motorschaden?',
    answer: 'Ja, auch Fahrzeuge mit Motorschaden kaufen wir an. Ob Getriebeschaden, defekte Turbolader oder andere mechanische Probleme – wir machen Ihnen ein Angebot.',
  },
  {
    question: 'Wie läuft die Bezahlung ab?',
    answer: 'Die Bezahlung erfolgt sofort bei Übergabe des Fahrzeugs. Sie können zwischen Barzahlung oder Überweisung wählen – ganz wie es Ihnen lieber ist.',
  },
];

export const FAQ = () => {
  return (
    <section id="faq" data-testid="faq-section" className="py-20 md:py-28 bg-tiktok-surface">
      <div className="container mx-auto px-4 md:px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-sm font-semibold uppercase tracking-[0.2em] text-tiktok-cyan mb-4 block">
            Häufige Fragen
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            FAQ
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Hier finden Sie Antworten auf die häufigsten Fragen rund um den Autoankauf.
          </p>
        </motion.div>

        {/* FAQ Accordion */}
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <AccordionItem
                  value={`item-${index}`}
                  data-testid={`faq-item-${index}`}
                  className="bg-tiktok-black border border-white/10 rounded-xl px-6 overflow-hidden"
                >
                  <AccordionTrigger 
                    className="faq-trigger text-left text-white hover:text-tiktok-cyan py-5 hover:no-underline"
                  >
                    <span className="font-semibold pr-4">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400 pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>
        </div>

        {/* Documents Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto mt-12"
        >
          <div className="bg-gradient-to-r from-tiktok-cyan/10 to-tiktok-pink/10 rounded-2xl border border-white/10 p-6 md:p-8">
            <h3 className="font-heading text-xl font-bold text-white mb-4">
              Benötigte Unterlagen für den Ankauf
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-tiktok-cyan/20 flex items-center justify-center">
                  <span className="text-tiktok-cyan font-bold">1</span>
                </div>
                <span className="text-gray-300">Fahrzeugschein (Zul. Teil I)</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-tiktok-cyan/20 flex items-center justify-center">
                  <span className="text-tiktok-cyan font-bold">2</span>
                </div>
                <span className="text-gray-300">Fahrzeugbrief (Zul. Teil II)</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-tiktok-cyan/20 flex items-center justify-center">
                  <span className="text-tiktok-cyan font-bold">3</span>
                </div>
                <span className="text-gray-300">Alle Fahrzeugschlüssel</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-tiktok-pink/20 flex items-center justify-center">
                  <span className="text-tiktok-pink font-bold">+</span>
                </div>
                <span className="text-gray-300">Serviceheft (optional)</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
